<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, reactive } from 'vue'

import { ParagraphType } from '@xcel-next/core/enums'

import { useAdaStore } from '../stores/ada'
import { isUrlResponsibleSlider } from '../utils/slider-initial-index'
import GooglePreferredSourceButton from './content-header/GooglePreferredSourceButton.vue'
/**
 * Some Paragraph Types return raw HTML to be injected into the component
 * These will be of type: text, raw, video, embed
 * To avoid duplication of one-liner components, we will use the same component for all of those types.
 */
import {
  ParagraphTypeAdaAdSlot,
  ParagraphTypeAdaNativeCrossbar,
  ParagraphTypeAdaAutoNative,
  ParagraphTypeAdaPagestrip,
  ParagraphTypeAdditionalLinks,
  ParagraphTypeAffiliateLink,
  ParagraphTypeFaq,
  ParagraphTypeAuthorBox,
  ParagraphTypeButtonGroup,
  ParagraphTypeChildTagsTeaserGroup,
  ParagraphTypeContactForm,
  ParagraphTypeContentBox,
  ParagraphTypeEmbed,
  ParagraphTypeFileDownload,
  ParagraphTypeHorizontalLine,
  ParagraphTypeHoroscope,
  ParagraphTypeHotOrNot,
  ParagraphTypeHtml,
  ParagraphTypeIframe,
  ParagraphTypeImage,
  ParagraphTypeInlineTeaser,
  ParagraphTypeInterline,
  ParagraphTypeLexicon,
  ParagraphTypeListOfSources,
  ParagraphTypeMoonRun,
  ParagraphTypeNewsletterTeaser,
  ParagraphTypeAscendantCalculator,
  ParagraphTypePoll,
  ParagraphTypeProductGroup,
  ParagraphTypeQuote,
  ParagraphTypeRecipe,
  ParagraphTypeRelatedTags,
  ParagraphTypeSearch,
  ParagraphTypeSlider,
  ParagraphTypeSocialStrip,
  ParagraphTypeStructureTermTree,
  ParagraphTypeTable,
  ParagraphTypeTableExt,
  ParagraphTypeTableOfContents,
  ParagraphTypeTeaserGroup,
  ParagraphTypeVideo,
  ParagraphTypeVerticalVideo,
  ParagraphTypeWebForm,
  ParagraphTypeProductAutoComparison,
  ParagraphTypeInfoBar,
  ParagraphTypeVodRating,
  ParagraphTypeVodEpgBroadcastSlider,
  ParagraphTypeVodStreamingCard,
  ParagraphTypeVodStreamingOptions,
  ParagraphTypeVodSeasonWithEpisodes,
  ParagraphTypeEpgBroadcastRating,
  ParagraphTypeEpgBroadcastReruns,
  ParagraphTypeLogoSlider,
  ParagraphTypeEpgWidget,
  ParagraphTypeVodTeaserForEpgBroadcast,
  ParagraphTypeAdDividingLine,
  ParagraphTypeTabbedInfo,
  AdaSlotMarker,
  ParagraphTypeCarOfferWidget,
  ParagraphTypeRecipeAggregateRating,
  ParagraphTypeUserActivation,
  ParagraphTypeUserLogin,
  ParagraphTypeUserPasswordChange,
  ParagraphTypeUserPasswordReset,
  ParagraphTypeUserRegister,
  ParagraphTypeUserSubscription
} from './paragraph-types'

import type { Component } from 'vue'
import type { ComposedMetaData, ComposedPageMeta } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'
import type { RenderedParagraph } from '../utils/paragraph-schema/paragraphs'

defineOptions({ name: 'Paragraphs' })

const props = withDefaults(defineProps<{
  // The collection of paragraphs
  paragraphs?: RenderedParagraph[],
  metaData?: ComposedMetaData,
  brand?: Brand,
  includeDepth?: number,
  // eslint-disable-next-line vue/no-unused-properties -- declared only to avoid falling through onto the root element
  pageType?: string,
  pageMeta?: ComposedPageMeta,
  dataIsSliderChild?: boolean,
  dataSliderInitialSlide?: boolean,
  dataSliderPlayerActive?: boolean,
  prioritizeFirstTeaserImage?: boolean,
  isPreview?: boolean
}>(), {
  paragraphs: () => [],
  includeDepth: 0,
  pageMeta: () => ({}),
  dataIsSliderChild: false,
  dataSliderInitialSlide: false,
  dataSliderPlayerActive: true,
  prioritizeFirstTeaserImage: false,
  isPreview: false
})

const { dynamicAdSlotsEnabled, blockAds, staticMobile1OnHome } = storeToRefs(useAdaStore())

// <script setup> imports aren't resolvable by string name; unknown names fall back to global (brand) registrations
const vueComponentParagraphTypes: Record<string, Component> = {
  ParagraphTypeUserActivation,
  ParagraphTypeUserLogin,
  ParagraphTypeUserPasswordChange,
  ParagraphTypeUserPasswordReset,
  ParagraphTypeUserRegister,
  ParagraphTypeUserSubscription
}

const typeCounter: Record<string, number> = reactive({})
const countedParagraphs: Record<string, number> = reactive({})

const allowNativeTeasers = computed(() => !props.metaData?.disallowNativeTeasers)

const getTeaserGroupSliderNo = (paragraph: RenderedParagraph) =>
  (paragraph.listOrderPosition ?? 0) + props.includeDepth + typeCounter[paragraph.paragraphType]

const addClassOnLastHighlightedInRow = (index: number) => {
  if (index >= 0) {
    const prevIndex = index - 1
    const nextIndex = index + 1
    const classes: string[] = []
    if (props.paragraphs[index].teaserGroup && props.paragraphs[index].teaserGroup?.highlighted === true) {
      if (props.paragraphs[prevIndex] === undefined ||
        (props.paragraphs[prevIndex] && props.paragraphs[prevIndex].paragraphType !== ParagraphType.teaserGroup) ||
        (props.paragraphs[prevIndex].paragraphType === ParagraphType.teaserGroup && props.paragraphs[prevIndex].teaserGroup && props.paragraphs[prevIndex].teaserGroup?.highlighted !== true)) {
        classes.push('bx-teaser-container--highlight-first')
      }
      if (props.paragraphs[nextIndex] === undefined ||
        (props.paragraphs[nextIndex] && props.paragraphs[nextIndex].paragraphType !== ParagraphType.teaserGroup) ||
        (props.paragraphs[nextIndex].paragraphType === ParagraphType.teaserGroup && props.paragraphs[nextIndex].teaserGroup && props.paragraphs[nextIndex].teaserGroup?.highlighted !== true)) {
        classes.push('bx-teaser-container--highlight-followup')
      }
    }
    return classes
  }
}

const kebabCase = (inputString: string) =>
  inputString && inputString.length > 0 ? inputString.replace(/([A-Z])/g, '-$1').toLowerCase() : ''

const addSelectorClass = (type: string, index: number) => {
  const countIndex = `${props.includeDepth}-${index}`
  if (!countedParagraphs[countIndex]) {
    typeCounter[type] = typeCounter[type] === undefined ? 1 : typeCounter[type] + 1
    countedParagraphs[countIndex] = typeCounter[type]
  }
  return `bx-content--${kebabCase(type)} bx-js-${kebabCase(type)}-${props.includeDepth}-${countedParagraphs[countIndex]}`
}
</script>

<template v-if="paragraphs?.length > 0">
  <div class="bx-paragraph-list">
    <div v-for="(paragraph, index) in paragraphs"
         :key="index"
         :data-lop="paragraph.listOrderPosition"
         :class="[addSelectorClass(paragraph.paragraphType, index), addClassOnLastHighlightedInRow(index), { 'bx-collection bx-ada--slot-wrapper': paragraph.enableRectangle1 }]">
      <component :is="vueComponentParagraphTypes[paragraph.vueComponent.componentName] ?? paragraph.vueComponent.componentName"
                 v-if="paragraph.paragraphType === ParagraphType.vueComponent && paragraph.vueComponent?.componentName"
                 :params="paragraph.vueComponent.params" />
      <ParagraphTypeHtml v-else-if="paragraph.paragraphType === ParagraphType.text"
                         :html="paragraph.text"
                         :meta-data="metaData" />
      <ParagraphTypeHtml v-else-if="paragraph.paragraphType === ParagraphType.raw"
                         :html="paragraph.raw"
                         :meta-data="metaData" />
      <ParagraphTypeEmbed v-else-if="paragraph.paragraphType === ParagraphType.embed"
                          :embed="paragraph.embed" />
      <ParagraphTypeRecipe v-else-if="paragraph.paragraphType === ParagraphType.recipe"
                           :brand="brand"
                           :recipe="paragraph.recipe"
                           :page-meta="pageMeta">
        <!-- eslint-disable vue/valid-v-slot -->
        <template v-for="(_, slot) of $slots"
                  #[slot]="scope">
          <slot :name="slot"
                v-bind="scope" />
        </template>
        <!-- eslint-enable vue/valid-v-slot -->
      </ParagraphTypeRecipe>
      <ParagraphTypeRecipeAggregateRating v-else-if="paragraph.paragraphType === ParagraphType.recipeAggregateRating"
                                          :recipe-aggregate-rating="paragraph.recipeAggregateRating" />
      <ParagraphTypeIframe v-else-if="paragraph.paragraphType === ParagraphType.iframe"
                           v-bind="paragraph.iframe"
                           :include-depth="includeDepth" />
      <ParagraphTypeHorizontalLine v-else-if="paragraph.paragraphType === ParagraphType.horizontalLine && paragraph.horizontalLine"
                                   :horizontal-line="paragraph.horizontalLine" />
      <ParagraphTypeInterline v-else-if="paragraph.paragraphType === ParagraphType.interline"
                              :interline="paragraph.interline" />
      <ParagraphTypeImage v-else-if="paragraph.paragraphType === ParagraphType.image && paragraph.image"
                          :image="paragraph.image"
                          :brand="brand"
                          :list-order-position="paragraph.listOrderPosition"
                          :type-count="typeCounter['image']" />
      <ParagraphTypeQuote v-else-if="paragraph.paragraphType === ParagraphType.quote"
                          :quote="paragraph.quote"
                          :page-meta="pageMeta" />
      <ParagraphTypeSlider v-else-if="paragraph.paragraphType === ParagraphType.slider"
                           v-bind="paragraph.slider"
                           :slider-no="index"
                           :list-order-position="paragraph.listOrderPosition"
                           :single-slide-urls="isUrlResponsibleSlider(paragraphs, index, includeDepth)"
                           :brand="brand"
                           :meta-data="metaData"
                           :include-depth="includeDepth" />
      <ParagraphTypeSocialStrip v-else-if="paragraph.paragraphType === ParagraphType.socialStrip"
                                :social-strip="paragraph.socialStrip" />
      <ParagraphTypeVideo v-else-if="paragraph.paragraphType === ParagraphType.video"
                          :video="paragraph.video"
                          :list-order-position="paragraph.listOrderPosition"
                          :data-is-slider-child="dataIsSliderChild"
                          :data-slider-initial-slide="dataSliderInitialSlide"
                          :data-slider-player-active="dataSliderPlayerActive"
                          :include-depth="includeDepth" />
      <ParagraphTypeVerticalVideo v-else-if="paragraph.paragraphType === ParagraphType.verticalVideo"
                                  v-bind="paragraph.verticalVideo" />
      <ParagraphTypeWebForm v-else-if="paragraph.paragraphType === ParagraphType.webForm"
                            v-bind="paragraph.webForm" />
      <ParagraphTypeRelatedTags v-else-if="paragraph.paragraphType === ParagraphType.relatedTags"
                                :title="paragraph.relatedTags?.title"
                                :tags="paragraph.relatedTags?.tags" />
      <ParagraphTypeTeaserGroup v-else-if="paragraph.paragraphType === ParagraphType.teaserGroup || paragraph.paragraphType === ParagraphType.infoTeaser || paragraph.paragraphType === ParagraphType.vodEpgTeaserGroup"
                                v-bind="paragraph.teaserGroup || paragraph.infoTeaser || paragraph.vodEpgTeaserGroup"
                                :include-depth="includeDepth"
                                :list-order-position="paragraph.listOrderPosition"
                                :data-is-slider-child="dataIsSliderChild"
                                :data-slider-initial-slide="dataSliderInitialSlide"
                                :prioritize-first-image="prioritizeFirstTeaserImage"
                                :slider-no="getTeaserGroupSliderNo(paragraph)"
                                :brand="brand" />
      <ParagraphTypeProductGroup v-else-if="paragraph.paragraphType === ParagraphType.productGroup"
                                 :product-group="paragraph.productGroup"
                                 :brand="brand"
                                 :is-content-commerce="metaData?.isContentCommerce"
                                 :slider-no="paragraph.listOrderPosition" />
      <ParagraphTypeProductAutoComparison v-else-if="paragraph.paragraphType === ParagraphType.productAutoComparison"
                                          :product-auto-comparison="paragraph.productAutoComparison"
                                          :is-content-commerce="metaData?.isContentCommerce"
                                          :brand="brand" />
      <ParagraphTypeChildTagsTeaserGroup v-else-if="paragraph.paragraphType === ParagraphType.childTagsTeaserGroup"
                                         v-bind="paragraph.childTagsTeaserGroup"
                                         :include-depth="includeDepth"
                                         :list-order-position="paragraph.listOrderPosition"
                                         :brand="brand" />
      <ParagraphTypeTeaserGroup v-else-if="paragraph.paragraphType === ParagraphType.pageableTeaserGroup"
                                v-bind="paragraph.pageableTeaserGroup"
                                :include-depth="includeDepth"
                                :list-order-position="paragraph.listOrderPosition"
                                :slider-no="getTeaserGroupSliderNo(paragraph)"
                                :brand="brand" />
      <ParagraphTypeInlineTeaser v-else-if="paragraph.paragraphType === ParagraphType.teaser"
                                 :composed-teaser="paragraph.teaser"
                                 :list-order-position="paragraph.listOrderPosition"
                                 :meta-data="{brand: brand}" />
      <ParagraphTypeStructureTermTree v-else-if="paragraph.paragraphType === ParagraphType.structureTermTree"
                                      :structure-term-tree="paragraph.structureTermTree" />
      <ParagraphTypeAuthorBox v-else-if="paragraph.paragraphType === ParagraphType.authorBox"
                              :author-box="paragraph.authorBox"
                              :brand="brand" />
      <ParagraphTypeContentBox v-else-if="paragraph.paragraphType === ParagraphType.contentBox"
                               :content-box="paragraph.contentBox"
                               :brand="brand" />
      <ParagraphTypeFaq v-else-if="paragraph.paragraphType === ParagraphType.faq"
                        :items="paragraph.faq?.items" />
      <ParagraphTypeAffiliateLink v-else-if="paragraph.paragraphType === ParagraphType.affiliateLink && paragraph.affiliateLink"
                                  :affiliate-link="paragraph.affiliateLink"
                                  :is-image-overlay="paragraph.isImageOverlay" />
      <ParagraphTypeAdditionalLinks v-else-if="paragraph.paragraphType === ParagraphType.additionalLinks"
                                    :additional-links="paragraph.additionalLinks"
                                    :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeListOfSources v-else-if="paragraph.paragraphType === ParagraphType.listOfSources"
                                  :list-of-sources="paragraph.listOfSources"
                                  :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeAdaAdSlot v-else-if="paragraph.paragraphType === 'adaAdSlot'"
                              v-bind="paragraph.adaAdSlot" />
      <ParagraphTypeAdaAutoNative v-else-if="paragraph.paragraphType === 'adaAutoNative'" />
      <ParagraphTypeAdaPagestrip v-else-if="paragraph.paragraphType === 'adaPagestrip'" />
      <ParagraphTypeAdaNativeCrossbar v-else-if="paragraph.paragraphType === 'adaNativeCrossbar' && allowNativeTeasers"
                                      v-bind="paragraph.adaNativeCrossbar" />
      <ParagraphTypeTable v-else-if="paragraph.paragraphType === ParagraphType.table && paragraph.table"
                          :table="paragraph.table" />
      <ParagraphTypeTableExt v-else-if="paragraph.paragraphType === ParagraphType.tableExt && paragraph.tableExt"
                             :table-ext="paragraph.tableExt" />
      <ParagraphTypeTableOfContents v-else-if="paragraph.paragraphType === ParagraphType.tableOfContents"
                                    :table-of-contents="paragraph.tableOfContents" />
      <ParagraphTypeHotOrNot v-else-if="paragraph.paragraphType === ParagraphType.hotOrNot && paragraph.hotOrNot"
                             :hot-or-not="paragraph.hotOrNot"
                             :brand="brand" />
      <ParagraphTypePoll v-else-if="paragraph.paragraphType === ParagraphType.poll && paragraph.poll"
                         :poll="paragraph.poll"
                         :list-order-position="paragraph.listOrderPosition"
                         :brand="brand" />
      <ParagraphTypeNewsletterTeaser v-else-if="paragraph.paragraphType === ParagraphType.newsletterTeaser" />
      <ParagraphTypeAscendantCalculator v-else-if="paragraph.paragraphType === ParagraphType.ascendantCalculator" />
      <ParagraphTypeHoroscope v-else-if="paragraph.paragraphType === ParagraphType.horoscope"
                              :paragraph="paragraph.horoscope"
                              :brand="brand" />
      <ParagraphTypeMoonRun v-else-if="paragraph.paragraphType === ParagraphType.moonrun"
                            :moon-run="paragraph.moonrun" />
      <ParagraphTypeButtonGroup v-else-if="paragraph.paragraphType === ParagraphType.buttonGroup"
                                :button-group="paragraph.buttonGroup" />
      <ParagraphTypeFileDownload v-else-if="paragraph.paragraphType === ParagraphType.fileDownload"
                                 :file-download="paragraph.fileDownload"
                                 :brand="brand" />
      <ParagraphTypeLexicon v-else-if="paragraph.paragraphType === ParagraphType.lexicon"
                            :lexicon="paragraph.lexicon"
                            :include-depth="includeDepth"
                            :brand="brand" />
      <ParagraphTypeSearch v-else-if="paragraph.paragraphType === ParagraphType.search"
                           :search="paragraph.search"
                           :brand="brand" />
      <ParagraphTypeContactForm v-else-if="paragraph.paragraphType === ParagraphType.contactForm"
                                :contact-form="paragraph.contactForm"
                                :brand="brand" />
      <ParagraphTypeInfoBar v-else-if="paragraph.paragraphType === ParagraphType.infoBar"
                            :info-bar="paragraph.infoBar" />
      <ParagraphTypeVodRating v-else-if="paragraph.paragraphType === ParagraphType.vodRating"
                              :vod-rating="paragraph.vodRating" />
      <ParagraphTypeVodEpgBroadcastSlider v-else-if="paragraph.paragraphType === ParagraphType.vodEpgBroadcastSlider"
                                          :trailer-items="paragraph.vodEpgBroadcastSlider?.trailerItems"
                                          :image-items="paragraph.vodEpgBroadcastSlider?.imageItems"
                                          :brand="brand"
                                          :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeVodStreamingCard v-else-if="paragraph.paragraphType === ParagraphType.vodStreamingCard"
                                     :vod-streaming-card="paragraph.vodStreamingCard" />
      <ParagraphTypeVodStreamingOptions v-else-if="paragraph.paragraphType === ParagraphType.vodStreamingOptions"
                                        :vod-streaming-options="paragraph.vodStreamingOptions" />
      <ParagraphTypeVodSeasonWithEpisodes v-else-if="paragraph.paragraphType === ParagraphType.vodSeasonWithEpisodes"
                                          :vod-season-with-episodes="paragraph.vodSeasonWithEpisodes"
                                          :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeEpgBroadcastRating v-else-if="paragraph.paragraphType === ParagraphType.epgBroadcastRating"
                                       :epg-broadcast-rating="paragraph.epgBroadcastRating" />
      <ParagraphTypeEpgBroadcastReruns v-else-if="paragraph.paragraphType === ParagraphType.epgBroadcastReruns"
                                       :epg-broadcast-reruns="paragraph.epgBroadcastReruns"
                                       :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeLogoSlider v-else-if="paragraph.paragraphType === ParagraphType.logoSlider"
                               :logo-slider="paragraph.logoSlider"
                               :list-order-position="paragraph.listOrderPosition" />
      <ParagraphTypeEpgWidget v-else-if="paragraph.paragraphType === ParagraphType.epgWidget"
                              :epg-widget="paragraph.epgWidget"
                              :is-preview="isPreview" />
      <ParagraphTypeVodTeaserForEpgBroadcast v-else-if="paragraph.paragraphType === ParagraphType.vodTeaserForEpgBroadcast"
                                             :vod-teaser-for-epg-broadcast="paragraph.vodTeaserForEpgBroadcast" />
      <ParagraphTypeAdDividingLine v-else-if="paragraph.paragraphType === ParagraphType.adDividingLine"
                                   v-bind="paragraph" />
      <ParagraphTypeTabbedInfo v-else-if="paragraph.paragraphType === ParagraphType.tabbedInfo"
                               :tabbed-info="paragraph.tabbedInfo" />
      <ParagraphTypeCarOfferWidget v-else-if="paragraph.paragraphType === ParagraphType.carOfferWidget && paragraph.carOfferWidget"
                                   :car-offer-widget="paragraph.carOfferWidget"
                                   :page-meta="pageMeta"
                                   :brand="brand" />

      <GooglePreferredSourceButton v-if="index === 0 && includeDepth === 0 && !metaData?.hideGooglePreferredSourcesButton" />

      <ParagraphTypeAdaAdSlot v-if="index === 0 && includeDepth === 0 && staticMobile1OnHome"
                              slot-name="mobile"
                              :slot-id="1" />
      <ParagraphTypeAdaAdSlot v-else-if="paragraph.enableRectangle1"
                              slot-name="rectangle"
                              :slot-id="1" />
      <ParagraphTypeAdaAdSlot v-else-if="index === 0 && includeDepth === 0 && metaData?.isVideoOnlyArticle"
                              slot-name="outstream_div"
                              :slot-id="0" />
      <AdaSlotMarker v-else-if="dynamicAdSlotsEnabled && !blockAds && includeDepth === 0"
                     v-bind="{ aboveParagraph: { index: paragraph.paragraphType + '-' + index, type: paragraph.paragraphType } }" />
    </div>
  </div>
</template>
